/* eslint-disable react-refresh/only-export-components */
import {
  faArrowLeftFromBracket,
  faCaretUp,
  faCheck,
  faCircleQuestion,
  faSort,
  faSortDown,
  faSortUp,
} from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { cva, type VariantProps } from "class-variance-authority"
import type { ComponentProps, ReactNode } from "react"
import { cn } from "cn"

/**
 * V2-Table (Figma „Tables"): `Table Header` + `Table Cell / Header` +
 * `Table Cell / Content` + `Deviation`. Technisch native `<table>`-Elemente
 * (shadcn-Struktur), die Props von Zelle/Header kommen aus Figma
 * (`type`, `sorted`, `showBottomLine`, `showPopoverButton`, …).
 */

function Table({ className, ...props }: ComponentProps<"table">) {
  return (
    <table
      data-slot="table"
      className={cn("w-full border-separate border-spacing-0", className)}
      {...props}
    />
  )
}

function TableHeader({ className, ...props }: ComponentProps<"thead">) {
  return <thead data-slot="table-header" className={cn(className)} {...props} />
}

function TableBody({ className, ...props }: ComponentProps<"tbody">) {
  return <tbody data-slot="table-body" className={cn(className)} {...props} />
}

function TableRow({ className, ...props }: ComponentProps<"tr">) {
  return <tr data-slot="table-row" className={cn(className)} {...props} />
}

/** Figma „Table Cell / Header", Achse `sorted`. */
export type TableHeadSorted = "none" | "unsorted" | "ascending" | "descending"

const sortIcons = {
  unsorted: faSort,
  ascending: faSortUp,
  descending: faSortDown,
} as const

const ariaSort: Record<
  TableHeadSorted,
  "none" | "ascending" | "descending" | undefined
> = {
  none: undefined,
  unsorted: "none",
  ascending: "ascending",
  descending: "descending",
} as const

export type TableHeadProps = Omit<ComponentProps<"th">, "onClick"> & {
  /** `none` = nicht sortierbar; `unsorted`/`ascending`/`descending` = sortierbar mit Icon. */
  sorted?: TableHeadSorted
  /** Sortierbare Spalten: Klick auf Label/Icon. */
  onSort?: () => void
  showBottomLine?: boolean
  showPopoverButton?: boolean
  /** Klick auf den Info-Button (z. B. Popover öffnen). */
  onPopoverButtonClick?: () => void
  popoverButtonLabel?: string
}

function TableHead({
  className,
  children,
  sorted = "none",
  onSort,
  showBottomLine = true,
  showPopoverButton = false,
  onPopoverButtonClick,
  popoverButtonLabel = "Info",
  ...props
}: TableHeadProps) {
  const label = (
    <>
      {children}
      {sorted !== "none" && (
        <FontAwesomeIcon
          icon={sortIcons[sorted]}
          className="size-[13.2px] shrink-0 text-neutrals-v2-700"
        />
      )}
    </>
  )

  return (
    <th
      data-slot="table-head"
      data-sorted={sorted}
      aria-sort={ariaSort[sorted]}
      className={cn(
        "h-10 px-1 text-left align-middle text-xs leading-4 font-semibold text-labels-secondary",
        showBottomLine && "border-b border-separators-non-opaque",
        className
      )}
      {...props}
    >
      <div className="flex items-center gap-1 whitespace-nowrap">
        {sorted === "none" ? (
          <span className="flex items-center gap-1">{label}</span>
        ) : (
          <button
            type="button"
            onClick={onSort}
            className="flex cursor-pointer items-center gap-1 rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-brand-blue-100"
          >
            {label}
          </button>
        )}
        {showPopoverButton && (
          <button
            type="button"
            data-slot="table-head-popover-button"
            aria-label={popoverButtonLabel}
            onClick={onPopoverButtonClick}
            className="flex h-6 min-w-6 cursor-pointer items-center justify-center rounded-full px-1 text-neutrals-v2-700 outline-none hover:text-neutrals-v2-900 focus-visible:ring-2 focus-visible:ring-brand-blue-100"
          >
            <FontAwesomeIcon icon={faCircleQuestion} className="size-3.5" />
          </button>
        )}
      </div>
    </th>
  )
}

/** Figma „Table Cell / Content", Achse `type` (normalisiert; „Avatar Goup" → `avatar-group`, „Arbeitszeit läuft" → `running`). */
export type TableCellType =
  | "text"
  | "avatar"
  | "icon"
  | "badge"
  | "input"
  | "switch"
  | "checkbox"
  | "actions"
  | "key"
  | "avatar-group"
  | "duration"
  | "time"
  | "information"
  | "running"

// Padding/Layout je Typ laut Figma: Actions haben links 0, sonst px-1.
const tableCellVariants = cva("h-14 overflow-clip align-middle", {
  variants: {
    type: {
      text: "px-1",
      avatar: "px-1",
      icon: "px-1",
      badge: "px-1",
      input: "px-1",
      switch: "px-1",
      checkbox: "px-1",
      actions: "pr-1 pl-0",
      key: "px-1",
      "avatar-group": "px-1",
      duration: "px-1",
      time: "px-1",
      information: "px-1",
      running: "px-1",
    },
  },
  defaultVariants: { type: "text" },
})

export type TableDeviationStatus =
  "positive" | "critical" | "negative" | "on-time" | "neutral"

const deviationColor: Record<TableDeviationStatus, string> = {
  positive: "text-accent-green-100",
  "on-time": "text-accent-green-100",
  critical: "text-accent-orange-100",
  negative: "text-brand-red-200",
  neutral: "text-neutrals-v2-700",
}

const deviationIcon = {
  positive: faCaretUp,
  critical: faCaretUp,
  negative: faCaretUp,
  "on-time": faCheck,
  neutral: faArrowLeftFromBracket,
}

export type TableDeviationProps = ComponentProps<"div"> & {
  /** Figma „Deviation", Achse `status` (Positiv/Kritisch/Negativ/Pünktlich/Neutral). */
  status?: TableDeviationStatus
  icon?: ReactNode
  showLabel?: boolean
  value?: ReactNode
}

function TableDeviation({
  className,
  status = "positive",
  icon,
  showLabel = true,
  value,
  ...props
}: TableDeviationProps) {
  return (
    <div
      data-slot="table-deviation"
      data-status={status}
      className={cn(
        "inline-flex items-center gap-0.5 rounded-sm text-xs leading-4 font-semibold whitespace-nowrap",
        deviationColor[status],
        className
      )}
      {...props}
    >
      {icon ?? (
        <FontAwesomeIcon icon={deviationIcon[status]} className="size-3.5" />
      )}
      {showLabel && value}
    </div>
  )
}

export type TableCellProps = Omit<ComponentProps<"td">, "children"> &
  VariantProps<typeof tableCellVariants> & {
    type?: TableCellType
    showBottomLine?: boolean
    /** `text` / `avatar` / `icon` / `key` / `time`: Haupttext der Zelle. */
    text?: ReactNode
    description?: ReactNode
    showDescription?: boolean
    /** `text`: farbiger Punkt vor dem Text (CSS-Farbe, z. B. `var(--color-accent-yellow-100)`). */
    dotColor?: string
    showDot?: boolean
    /** `avatar`: Avatar-Element (z. B. `<Avatar type="initials" size="xs" />`). */
    avatar?: ReactNode
    /** `icon`: Icon vor dem Text. */
    icon?: ReactNode
    showText?: boolean
    /** `duration`: Stunden/Minuten (Figma „Wert 1/2"). */
    hours?: ReactNode
    minutes?: ReactNode
    /** `duration`/`time`: Abweichungs-Indikator. */
    deviation?: ReactNode
    showDeviation?: boolean
    /** `time`: Zusatzinfo „gestempelte Zeit". */
    stampedTime?: ReactNode
    showStampedTime?: boolean
    /**
     * Slot für Typen mit bestehenden V2-Komponenten: `badge` (Badge),
     * `input` (Input), `switch` (Switch), `checkbox` (Checkbox), `actions`
     * (Buttons), `key` (Copy-Button hinter dem Key), `avatar-group`,
     * `information` (Badges).
     */
    children?: ReactNode
  }

const slotLayout: Partial<Record<TableCellType, string>> = {
  actions: "gap-2",
  key: "gap-1",
  "avatar-group": "gap-1",
  information: "gap-1",
}

function TableCell({
  className,
  type = "text",
  showBottomLine = true,
  text,
  description,
  showDescription = false,
  dotColor,
  showDot = false,
  avatar,
  icon,
  showText = true,
  hours,
  minutes,
  deviation,
  showDeviation = true,
  stampedTime,
  showStampedTime = true,
  children,
  ...props
}: TableCellProps) {
  const descriptionNode = showDescription && description && (
    <p
      data-slot="table-cell-description"
      className="text-xs leading-4 font-normal text-labels-secondary"
    >
      {description}
    </p>
  )

  let content: ReactNode
  switch (type) {
    case "text":
      content = (
        <>
          <div className="flex h-5 items-center gap-2">
            {showDot && (
              <span
                data-slot="table-cell-dot"
                className="size-2.5 shrink-0 rounded-full"
                style={{ backgroundColor: dotColor }}
              />
            )}
            <p className="min-w-0 flex-1 text-sm leading-5 font-semibold text-labels-primary">
              {text}
            </p>
          </div>
          {descriptionNode}
        </>
      )
      break
    case "avatar":
      content = (
        <div className="flex items-center gap-2">
          {avatar}
          <div className="min-w-0 flex-1">
            <p className="text-sm leading-5 font-semibold text-labels-primary">
              {text}
            </p>
            {descriptionNode}
          </div>
        </div>
      )
      break
    case "icon":
      content = (
        <div className="flex items-center gap-2">
          {icon && (
            <span className="flex size-[18px] shrink-0 items-center justify-center text-labels-primary [&>svg]:size-full">
              {icon}
            </span>
          )}
          {showText && (
            <div className="min-w-0 flex-1">
              <p className="text-sm leading-5 font-semibold text-labels-primary">
                {text}
              </p>
              {descriptionNode}
            </div>
          )}
        </div>
      )
      break
    case "key":
      content = (
        <div className="flex items-center gap-1">
          <span
            data-slot="table-cell-key"
            className="flex h-9 items-center justify-center rounded-lg bg-neutrals-v2-200 px-2 text-sm leading-5 font-semibold whitespace-nowrap text-labels-primary"
          >
            {text}
          </span>
          {children}
        </div>
      )
      break
    case "duration":
      content = (
        <div className="flex items-center gap-1 text-sm leading-5 font-semibold whitespace-nowrap">
          <span className="flex gap-1">
            <span className="text-labels-primary">{hours}</span>
            <span className="text-labels-secondary">Std.</span>
          </span>
          <span className="flex gap-1">
            <span className="text-labels-primary">{minutes}</span>
            <span className="text-labels-secondary">Min.</span>
          </span>
          {showDeviation && deviation}
        </div>
      )
      break
    case "time":
      content = (
        <div className="flex items-center gap-1">
          <span className="text-sm leading-5 font-semibold whitespace-nowrap text-labels-primary">
            {text}
          </span>
          {showDeviation && deviation}
          {showStampedTime && stampedTime}
        </div>
      )
      break
    case "running":
      content = (
        <div className="flex items-center gap-[22px] pl-2">
          <span
            data-slot="table-cell-running-dot"
            className="size-3 shrink-0 rounded-full bg-brand-red-200 ring-6 ring-brand-red-200/20"
          />
          <span className="text-sm leading-5 font-semibold whitespace-nowrap text-labels-primary">
            {text ?? "Läuft ..."}
          </span>
        </div>
      )
      break
    default:
      // badge, input, switch, checkbox, actions, avatar-group, information
      content = (
        <div className={cn("flex items-center", slotLayout[type])}>
          {children}
        </div>
      )
  }

  return (
    <td
      data-slot="table-cell"
      data-type={type}
      className={cn(
        tableCellVariants({ type }),
        showBottomLine && "border-b border-separators-non-opaque",
        className
      )}
      {...props}
    >
      {content}
    </td>
  )
}

export {
  Table,
  TableBody,
  TableCell,
  TableDeviation,
  TableHead,
  TableHeader,
  TableRow,
  tableCellVariants,
}
